import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CloudSync,
  DataUsageOutlined,
  FileDownload,
  FunctionsOutlined,
  InfoOutlined,
  ListAltOutlined,
  Lock,
  SecurityOutlined,
  WorkOutlineOutlined,
} from '@mui/icons-material';
import { Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Estate_FieldsFragment,
  EstateBalanceSheet_FieldsFragment,
  EstateBalanceSheetAssetColumns,
  EstateBalanceSheetLiabilityColumns,
  EstateImportSource,
  EstateType,
  PortalPermissions,
  useEstateBalanceSheet_UpdateEstateBalanceSheetMutation,
  User_InvitePortalClientMutation,
} from 'queries';

import ClientShareModal from 'pages/common/client/client-share-modal';
import { EstateDashboardTab } from 'pages/estate-dashboard/estate-dashboard';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import useHasPortalPermission from 'utils/useHasPortalPermission';

import EstateBalanceSheetAssetCalculations from './estate-balance-sheet-calculations';
import EstateBalanceSheetClientDetails from './estate-balance-sheet-client-details';
import EstateBalanceSheetColumnVisibilityPanel from './estate-balance-sheet-column-visibility-panel';
import EstateBalanceSheetDropdownMenu from './estate-balance-sheet-dropdown-menu';
import EstateBalanceSheetFindAccountsButton from './estate-balance-sheet-find-accounts-button';
import { EstateBalanceSheetIntegrationsModal } from './estate-balance-sheet-integrations-modal';

const useStyles = makeStyles(() => ({
  actionButton: {
    background: '#F5F5F5',
    borderRadius: '8px',
    fontSize: '14px',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: '16px',
    marginBottom: '16px',
    color: '#1C2541',
  },
  actionButtonContainer: {
    marginBottom: '40px',
  },
}));

type Props = {
  estate: Estate_FieldsFragment;
  estateBalanceSheet: EstateBalanceSheet_FieldsFragment;
  page: 'estate-balance-sheet' | 'estate-accounting-summary';
  handleExportExcel?: () => void;
};

const EstateBalanceSheetDashboardMenu = ({
  estate,
  estateBalanceSheet,
  page,
  handleExportExcel,
}: Props) => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const hasPortalPermissionAdmin = useHasPortalPermission(PortalPermissions.Admin);
  const hasPortalPermissionUser = useHasPortalPermission(PortalPermissions.User);

  const [showClientShareModal, setShowClientShareModal] = useState(false);
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);

  const [updateEstateBalanceSheet] = useEstateBalanceSheet_UpdateEstateBalanceSheetMutation({
    onError: (e) => {
      showAlert({
        message: 'Error updating balance sheet: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const onToggleAssetsColumnVisibility = (columnName: EstateBalanceSheetAssetColumns) => {
    const hiddenAssetColumns = [...estateBalanceSheet.hiddenAssetColumns];
    if (hiddenAssetColumns.includes(columnName)) {
      const index = hiddenAssetColumns.indexOf(columnName);
      hiddenAssetColumns.splice(index, 1);
    } else {
      hiddenAssetColumns.push(columnName);
    }
    updateEstateBalanceSheet({
      variables: {
        input: {
          id: estateBalanceSheet.id,
          portalId,
          hiddenAssetColumns: hiddenAssetColumns,
          hiddenLiabilityColumns: estateBalanceSheet.hiddenLiabilityColumns,
        },
      },
    });
  };

  const onToggleLiabilitiesColumnVisibility = (columnName: EstateBalanceSheetLiabilityColumns) => {
    const hiddenLiabilityColumns = [...estateBalanceSheet.hiddenLiabilityColumns];
    if (hiddenLiabilityColumns.includes(columnName)) {
      const index = hiddenLiabilityColumns.indexOf(columnName);
      hiddenLiabilityColumns.splice(index, 1);
    } else {
      hiddenLiabilityColumns.push(columnName);
    }
    updateEstateBalanceSheet({
      variables: {
        input: {
          id: estateBalanceSheet.id,
          portalId,
          hiddenAssetColumns: estateBalanceSheet.hiddenAssetColumns,
          hiddenLiabilityColumns: hiddenLiabilityColumns,
        },
      },
    });
  };

  const handleShareComplete = (invitePortalClientData: User_InvitePortalClientMutation) => {
    if (invitePortalClientData.data.emailVerified) {
      showToast({
        message: `Client now has shared access to this estate`,
        severity: NotificationSeverity.SUCCESS,
      });
    } else {
      showToast({
        message: `Share invitation has been sent to client email successfully`,
        severity: NotificationSeverity.SUCCESS,
      });
    }
    setShowClientShareModal(false);
  };

  return (
    <>
      <div className={classes.actionButtonContainer}>
        <EstateBalanceSheetDropdownMenu
          title="Client Details"
          startIcon={<InfoOutlined />}
          menuItems={[
            {
              content: (
                <EstateBalanceSheetClientDetails
                  firstName={estate.estateTargetFirstName}
                  lastName={estate.estateTargetLastName}
                  dateOfDeath={
                    estate.type === EstateType.Truadmin
                      ? estate.estateTruadminFields?.decedentDateOfDeath
                      : null
                  }
                />
              ),
            },
          ]}
        />
        <Button
          className={classes.actionButton}
          startIcon={<WorkOutlineOutlined />}
          href={`/home/${portalId}/estate/${estate.id}/estate-dashboard?tab=${EstateDashboardTab.ESTATE_CHECKLIST}`}
        >
          Estate Dashboard
        </Button>
        <Button
          className={classes.actionButton}
          startIcon={<SecurityOutlined />}
          href={`/home/${portalId}/estate/${estate.id}/estate-dashboard?tab=${EstateDashboardTab.VAULT}`}
        >
          Vault
        </Button>
        {(hasPortalPermissionAdmin || hasPortalPermissionUser) && (
          <>
            <EstateBalanceSheetFindAccountsButton estate={estate} />
          </>
        )}
        <EstateBalanceSheetDropdownMenu
          title="Calculations"
          startIcon={<FunctionsOutlined />}
          menuItems={[
            {
              content: (
                <EstateBalanceSheetAssetCalculations estateBalanceSheet={estateBalanceSheet} />
              ),
            },
          ]}
        />
        {page === 'estate-balance-sheet' && (
          <Button
            className={classes.actionButton}
            startIcon={<DataUsageOutlined />}
            href={`/home/${portalId}/estate/${estate.id}/estate-accounting-summary`}
          >
            Accounting Summary
          </Button>
        )}
        {page === 'estate-accounting-summary' && (
          <Button
            className={classes.actionButton}
            startIcon={<ListAltOutlined />}
            href={`/home/${portalId}/estate/${estate.id}/estate-dashboard?tab=${EstateDashboardTab.BALANCE_SHEET}`}
          >
            Balance Sheet
          </Button>
        )}

        {(hasPortalPermissionAdmin || hasPortalPermissionUser) && (
          <>
            {page === 'estate-balance-sheet' && (
              <EstateBalanceSheetColumnVisibilityPanel
                estateBalanceSheet={estateBalanceSheet}
                onToggleAssetsColumnVisibility={onToggleAssetsColumnVisibility}
                onToggleLiabilitiesColumnVisibility={onToggleLiabilitiesColumnVisibility}
              />
            )}
            {
              // Only show the Integrations Button if the matter was imported from DecisionVault
              // We may want to remove this restriction to support aggregating data from multiple sources
            }
            {estate.estateImport &&
              estate.estateImport.source === EstateImportSource.DecisionVault && (
                <Button
                  className={classes.actionButton}
                  title="Integrations"
                  startIcon={<CloudSync />}
                  onClick={() => {
                    setShowIntegrationsModal(true);
                  }}
                >
                  Integrations
                </Button>
              )}

            <Button
              className={classes.actionButton}
              startIcon={<Lock />}
              onClick={() => {
                if (
                  !estate.estateClientFirstName ||
                  !estate.estateClientLastName ||
                  !estate.estateClientEmailAddress ||
                  !estate.estateClientPhoneNumber
                ) {
                  showAlert({
                    message:
                      'Cannot share to client because client first name last name, email address, and phone number are all required',
                    severity: NotificationSeverity.ERROR,
                  });
                  return;
                }

                setShowClientShareModal(true);
              }}
            >
              Share
            </Button>
            {handleExportExcel && (
              <Button
                className={classes.actionButton}
                startIcon={<FileDownload />}
                onClick={handleExportExcel}
              >
                Export to Excel
              </Button>
            )}
          </>
        )}
      </div>
      {estate &&
        estate.id &&
        estate.estateName &&
        estate.estateClientFirstName &&
        estate.estateClientLastName &&
        estate.estateClientEmailAddress &&
        estate.estateClientPhoneNumber && (
          <ClientShareModal
            estateId={estate.id}
            estateName={estate.estateName}
            clientEmailAddress={estate.estateClientEmailAddress}
            showModal={showClientShareModal}
            setShowModal={setShowClientShareModal}
            onShareComplete={handleShareComplete}
          />
        )}
      {showIntegrationsModal && (
        <EstateBalanceSheetIntegrationsModal
          showModal={showIntegrationsModal}
          setShowModal={setShowIntegrationsModal}
        />
      )}
    </>
  );
};

export default EstateBalanceSheetDashboardMenu;
